import type { SubmissionFile } from '../lib/types.ts';

// A file row as returned by the admin history endpoint: every column of
// submission_files plus a short-lived presigned download URL (null if the
// presign failed, so the row still renders without a working link).
export type FileHistoryItem = SubmissionFile & { download_url: string | null };

// One version row prepared for display: the live file(s) are 'current', any
// superseded (replaced/removed) ones are 'replaced'.
export type FileVersion = FileHistoryItem & { state: 'current' | 'replaced' };

// Order newest-first: live versions (superseded_at === null) first, then the
// rest, each group by created_at descending. ISO-8601 strings compare with `<`.
// Returns a new array; does not mutate the input.
export function orderFileHistory(files: FileHistoryItem[]): FileVersion[] {
  return [...files]
    .sort((a, b) => {
      const aCurrent = a.superseded_at === null;
      const bCurrent = b.superseded_at === null;
      if (aCurrent !== bCurrent) return aCurrent ? -1 : 1;
      // Within a group, most recently uploaded first.
      if (a.created_at !== b.created_at) return a.created_at < b.created_at ? 1 : -1;
      return 0;
    })
    .map((f) => ({ ...f, state: f.superseded_at === null ? 'current' : 'replaced' }));
}

// One policy-document (adoption) version as returned by the history endpoint:
// the live submission_adoptions row plus rows archived to
// submission_adoption_history on replace/clear. archived_at null = live.
export type AdoptionHistoryItem = {
  id: string;
  mode: 'adopted' | 'uploaded';
  file_path: string | null;
  acknowledged_at: string | null;
  option_key: string | null;
  /** When this version was originally recorded. */
  recorded_at: string;
  archived_at: string | null;
  archived_reason: 'replaced' | 'cleared' | null;
  download_url: string | null;
};

export type AdoptionVersion = AdoptionHistoryItem & {
  state: 'current' | 'replaced';
  /** Basename of the uploaded file, null for adopted-mode versions. */
  file_name: string | null;
};

// Order the policy-document versions for display: the live choice first, then
// archived ones newest-archive-first. Returns a new array; no mutation.
export function orderAdoptionHistory(adoptions: AdoptionHistoryItem[]): AdoptionVersion[] {
  return [...adoptions]
    .sort((a, b) => {
      const aCurrent = a.archived_at === null;
      const bCurrent = b.archived_at === null;
      if (aCurrent !== bCurrent) return aCurrent ? -1 : 1;
      const aAt = a.archived_at ?? a.recorded_at;
      const bAt = b.archived_at ?? b.recorded_at;
      if (aAt !== bAt) return aAt < bAt ? 1 : -1;
      return 0;
    })
    .map((a) => ({
      ...a,
      state: a.archived_at === null ? 'current' : 'replaced',
      file_name: a.file_path ? (a.file_path.split('/').pop() ?? null) : null,
    }));
}

// Human-readable byte size, e.g. 0 B, 512 B, 1.4 KB, 2.0 MB. Uses 1024-based
// units to match how the upload size limits elsewhere are phrased ("5 MB").
export function formatFileSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return '—';
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let value = bytes / 1024;
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }
  // One decimal place, trimming a trailing ".0" for whole values.
  const rounded = value >= 10 ? Math.round(value) : Math.round(value * 10) / 10;
  return `${rounded} ${units[unitIndex]}`;
}
